import React, { useState, useEffect } from 'react';
import { loanService, cashAdvanceService, employeeService } from '../services/hrService';
import toast from 'react-hot-toast';

const LOAN_TYPES = [
  { value: 'salary_loan', label: 'Salary Loan' },
  { value: 'emergency_loan', label: 'Emergency Loan' },
  { value: 'sss_loan', label: 'SSS Loan' },
  { value: 'pagibig_loan', label: 'Pag-IBIG Loan' },
  { value: 'company_loan', label: 'Company Loan' },
];

const LOAN_STATUSES = [
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'approved', label: 'Approved', color: 'bg-blue-100 text-blue-800' },
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800' },
  { value: 'completed', label: 'Completed', color: 'bg-gray-100 text-gray-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
];

const CA_STATUSES = [
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'approved', label: 'Approved', color: 'bg-blue-100 text-blue-800' },
  { value: 'disbursed', label: 'Disbursed', color: 'bg-green-100 text-green-800' },
  { value: 'deducted', label: 'Fully Deducted', color: 'bg-gray-100 text-gray-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
];

export default function Loans() {
  const [activeTab, setActiveTab] = useState('loans');
  const [loans, setLoans] = useState([]);
  const [cashAdvances, setCashAdvances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [showCAModal, setShowCAModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [selectedCA, setSelectedCA] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');

  // Loan form state
  const [loanForm, setLoanForm] = useState({
    employee: '',
    loan_type: 'salary_loan',
    principal_amount: '',
    interest_rate: '0',
    amortization_per_pay: '',
    term_months: '12',
    start_date: new Date().toISOString().split('T')[0],
    remarks: '',
  });

  // Cash Advance form state
  const [caForm, setCAForm] = useState({
    employee: '',
    amount: '',
    deduction_per_period: '',
    request_date: new Date().toISOString().split('T')[0],
    reason: '',
  });

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    remarks: '',
  });

  useEffect(() => {
    loadEmployees();
    if (activeTab === 'loans') {
      loadLoans();
    } else {
      loadCashAdvances();
    }
  }, [activeTab, filterStatus, filterEmployee]);

  const loadEmployees = async () => {
    try {
      const response = await employeeService.getAll({ is_active: true });
      setEmployees(response.data.results || response.data);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadLoans = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterEmployee) params.employee = filterEmployee;
      const response = await loanService.getAll(params);
      setLoans(response.data.results || response.data);
    } catch (error) {
      toast.error('Failed to load loans');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadCashAdvances = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterEmployee) params.employee = filterEmployee;
      const response = await cashAdvanceService.getAll(params);
      setCashAdvances(response.data.results || response.data);
    } catch (error) {
      toast.error('Failed to load cash advances');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLoan = async (e) => {
    e.preventDefault();
    try {
      // Calculate total amount with interest
      const principal = parseFloat(loanForm.principal_amount);
      const rate = parseFloat(loanForm.interest_rate) / 100;
      const totalAmount = principal * (1 + rate);

      await loanService.create({
        ...loanForm,
        total_amount: totalAmount.toFixed(2),
      });
      toast.success('Loan created successfully');
      setShowLoanModal(false);
      resetLoanForm();
      loadLoans();
    } catch (error) {
      toast.error('Failed to create loan');
      console.error(error);
    }
  };

  const handleCreateCA = async (e) => {
    e.preventDefault();
    try {
      await cashAdvanceService.create(caForm);
      toast.success('Cash advance created successfully');
      setShowCAModal(false);
      resetCAForm();
      loadCashAdvances();
    } catch (error) {
      toast.error('Failed to create cash advance');
      console.error(error);
    }
  };

  const handleApproveLoan = async (loan) => {
    if (!window.confirm('Are you sure you want to approve this loan?')) return;
    try {
      await loanService.approve(loan.id);
      toast.success('Loan approved');
      loadLoans();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to approve loan');
    }
  };

  const handleCancelLoan = async (loan) => {
    if (!window.confirm('Are you sure you want to cancel this loan?')) return;
    try {
      await loanService.cancel(loan.id);
      toast.success('Loan cancelled');
      loadLoans();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel loan');
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedLoan) return;
    try {
      await loanService.recordPayment(selectedLoan.id, paymentForm);
      toast.success('Payment recorded');
      setShowPaymentModal(false);
      setPaymentForm({ amount: '', remarks: '' });
      setSelectedLoan(null);
      loadLoans();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to record payment');
    }
  };

  const handleApproveCA = async (ca) => {
    if (!window.confirm('Are you sure you want to approve this cash advance?')) return;
    try {
      await cashAdvanceService.approve(ca.id);
      toast.success('Cash advance approved');
      loadCashAdvances();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to approve cash advance');
    }
  };

  const handleDisburseCA = async (ca) => {
    if (!window.confirm('Mark this cash advance as disbursed?')) return;
    try {
      await cashAdvanceService.disburse(ca.id);
      toast.success('Cash advance disbursed');
      loadCashAdvances();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to disburse cash advance');
    }
  };

  const handleCancelCA = async (ca) => {
    if (!window.confirm('Are you sure you want to cancel this cash advance?')) return;
    try {
      await cashAdvanceService.cancel(ca.id);
      toast.success('Cash advance cancelled');
      loadCashAdvances();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel cash advance');
    }
  };

  const resetLoanForm = () => {
    setLoanForm({
      employee: '',
      loan_type: 'salary_loan',
      principal_amount: '',
      interest_rate: '0',
      amortization_per_pay: '',
      term_months: '12',
      start_date: new Date().toISOString().split('T')[0],
      remarks: '',
    });
  };

  const resetCAForm = () => {
    setCAForm({
      employee: '',
      amount: '',
      deduction_per_period: '',
      request_date: new Date().toISOString().split('T')[0],
      reason: '',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
    }).format(amount || 0);
  };

  const getStatusBadge = (status, statuses) => {
    const statusObj = statuses.find(s => s.value === status);
    return statusObj ? (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusObj.color}`}>
        {statusObj.label}
      </span>
    ) : status;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Loans & Cash Advances</h1>
        <div className="space-x-2">
          {activeTab === 'loans' ? (
            <button
              onClick={() => setShowLoanModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Loan
            </button>
          ) : (
            <button
              onClick={() => setShowCAModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Cash Advance
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('loans')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'loans'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Loans
          </button>
          <button
            onClick={() => setActiveTab('cashAdvances')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'cashAdvances'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Cash Advances
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm flex gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
          <select
            value={filterEmployee}
            onChange={(e) => setFilterEmployee(e.target.value)}
            className="border rounded-lg px-3 py-2 min-w-[200px]"
          >
            <option value="">All Employees</option>
            {employees.map(emp => (
              <option key={emp.id} value={emp.id}>
                {emp.employee_no} - {emp.full_name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border rounded-lg px-3 py-2 min-w-[150px]"
          >
            <option value="">All Statuses</option>
            {(activeTab === 'loans' ? LOAN_STATUSES : CA_STATUSES).map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : activeTab === 'loans' ? (
        /* Loans Table */
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Loan Type</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Principal</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Per Pay</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loans.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    No loans found
                  </td>
                </tr>
              ) : (
                loans.map(loan => (
                  <tr key={loan.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{loan.employee_name}</div>
                      <div className="text-sm text-gray-500">{loan.employee_no}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {loan.loan_type_display || LOAN_TYPES.find(t => t.value === loan.loan_type)?.label}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(loan.principal_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(loan.amortization_per_pay)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 text-right font-medium">
                      {formatCurrency(loan.total_paid)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 text-right font-medium">
                      {formatCurrency(loan.remaining_balance)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {getStatusBadge(loan.status, LOAN_STATUSES)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                      <div className="flex justify-center gap-2">
                        {loan.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApproveLoan(loan)}
                              className="text-green-600 hover:text-green-800"
                              title="Approve"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleCancelLoan(loan)}
                              className="text-red-600 hover:text-red-800"
                              title="Cancel"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </>
                        )}
                        {loan.status === 'active' && (
                          <button
                            onClick={() => {
                              setSelectedLoan(loan);
                              setPaymentForm({ amount: loan.amortization_per_pay, remarks: '' });
                              setShowPaymentModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800"
                            title="Record Payment"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        /* Cash Advances Table */
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Per Period</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cashAdvances.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    No cash advances found
                  </td>
                </tr>
              ) : (
                cashAdvances.map(ca => (
                  <tr key={ca.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{ca.employee_name}</div>
                      <div className="text-sm text-gray-500">{ca.employee_no}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(ca.request_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(ca.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(ca.deduction_per_period)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 text-right font-medium">
                      {formatCurrency(ca.remaining_balance)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-[200px] truncate">
                      {ca.reason}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {getStatusBadge(ca.status, CA_STATUSES)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                      <div className="flex justify-center gap-2">
                        {ca.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApproveCA(ca)}
                              className="text-green-600 hover:text-green-800"
                              title="Approve"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleCancelCA(ca)}
                              className="text-red-600 hover:text-red-800"
                              title="Cancel"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </>
                        )}
                        {ca.status === 'approved' && (
                          <button
                            onClick={() => handleDisburseCA(ca)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Disburse"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* New Loan Modal */}
      {showLoanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">New Loan</h2>
              <button
                onClick={() => { setShowLoanModal(false); resetLoanForm(); }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateLoan} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                <select
                  value={loanForm.employee}
                  onChange={(e) => setLoanForm({ ...loanForm, employee: e.target.value })}
                  required
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.employee_no} - {emp.full_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Loan Type *</label>
                <select
                  value={loanForm.loan_type}
                  onChange={(e) => setLoanForm({ ...loanForm, loan_type: e.target.value })}
                  required
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {LOAN_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Principal Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={loanForm.principal_amount}
                    onChange={(e) => setLoanForm({ ...loanForm, principal_amount: e.target.value })}
                    required
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Interest Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={loanForm.interest_rate}
                    onChange={(e) => setLoanForm({ ...loanForm, interest_rate: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amortization Per Pay *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={loanForm.amortization_per_pay}
                    onChange={(e) => setLoanForm({ ...loanForm, amortization_per_pay: e.target.value })}
                    required
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Term (Months)</label>
                  <input
                    type="number"
                    min="1"
                    value={loanForm.term_months}
                    onChange={(e) => setLoanForm({ ...loanForm, term_months: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="12"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={loanForm.start_date}
                  onChange={(e) => setLoanForm({ ...loanForm, start_date: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={loanForm.remarks}
                  onChange={(e) => setLoanForm({ ...loanForm, remarks: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  rows="2"
                  placeholder="Optional notes..."
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowLoanModal(false); resetLoanForm(); }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Loan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Cash Advance Modal */}
      {showCAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">New Cash Advance</h2>
              <button
                onClick={() => { setShowCAModal(false); resetCAForm(); }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateCA} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                <select
                  value={caForm.employee}
                  onChange={(e) => setCAForm({ ...caForm, employee: e.target.value })}
                  required
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.employee_no} - {emp.full_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={caForm.amount}
                    onChange={(e) => setCAForm({ ...caForm, amount: e.target.value })}
                    required
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deduction Per Period *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={caForm.deduction_per_period}
                    onChange={(e) => setCAForm({ ...caForm, deduction_per_period: e.target.value })}
                    required
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Request Date</label>
                <input
                  type="date"
                  value={caForm.request_date}
                  onChange={(e) => setCAForm({ ...caForm, request_date: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea
                  value={caForm.reason}
                  onChange={(e) => setCAForm({ ...caForm, reason: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  rows="2"
                  placeholder="Reason for cash advance..."
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowCAModal(false); resetCAForm(); }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Cash Advance
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {showPaymentModal && selectedLoan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Record Payment</h2>
              <button
                onClick={() => { setShowPaymentModal(false); setSelectedLoan(null); }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Employee: <span className="font-medium">{selectedLoan.employee_name}</span></p>
              <p className="text-sm text-gray-600">Remaining Balance: <span className="font-medium text-red-600">{formatCurrency(selectedLoan.remaining_balance)}</span></p>
            </div>
            <form onSubmit={handleRecordPayment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={selectedLoan.remaining_balance}
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  required
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <input
                  type="text"
                  value={paymentForm.remarks}
                  onChange={(e) => setPaymentForm({ ...paymentForm, remarks: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="e.g., Payroll deduction, Manual payment"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowPaymentModal(false); setSelectedLoan(null); }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
